import Description from "../../Common/EditForm/Description";
import FieldError from "../../Common/EditForm/FieldError";
import Label from "../../Common/EditForm/Label";
import SimplifiedFormRowProps from "../../Common/EditForm/SimplifiedFormRowProps";
import { appendValidOrInvalidClassName } from "../../Common/EditForm/appendErrorClassNames";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { InputGroup, Form, Button } from "react-bootstrap";
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";
import BusinessDocumentSelectorDialog from "./BusinessDocumentSelectorDialog";
import businessDocumentVM from "./businessDocumentVM";
import selectorSection from "./selectorSection";
import { AlertDialog } from "../../Common/Dialog/AlertDialog";

export type BusinessDocumentSelectorHandle = {
  openSelector: () => void;
};

export interface BusinessDocumentSelectorOwnProps {
  required?: boolean;
  selectorSectionId: selectorSection;
  onDocumentChanged?: (businessDocument: businessDocumentVM) => void;
  locked?: boolean;
}

export type BusinessDocumentSelectorProps<T extends FieldValues> =
  SimplifiedFormRowProps &
    BusinessDocumentSelectorOwnProps &
    UseControllerProps<T> & { control: Control<T> };

function BusinessDocumentSelectorFormRowInner<T extends FieldValues>(
  props: BusinessDocumentSelectorProps<T>,
  ref: React.ForwardedRef<BusinessDocumentSelectorHandle>
) {
  let localProps = { ...props };

  localProps.rules = { ...props.rules };
  localProps.rules.validate = { ...props.rules?.validate };

  if (!!props.required) {
    // we intentionally don't use rules.required, as it has its strong typing
    localProps.rules.validate.valueRequired = (
      value: businessDocumentVM | null
    ): string | undefined =>
      value !== null ? undefined : "Pole jest wymagane.";
  }

  const { field, fieldState } = useController(localProps);

  const isTouched = fieldState.isTouched;
  const error = fieldState.error;

  let [isSelectorShown, setSelectorShown] = useState<boolean>(false);

  let [value, setValue] = useState<businessDocumentVM | null>(null);

  const locked = props.locked || false;

  /** Opens selector */
  const openSelector = useCallback(async () => {
    if (locked) {
      await AlertDialog(
        "Nie można zmienić",
        "Dodałeś już pozycję odnoszącą się do tego dokumentu sprzedaży. Jeżeli była to pomyłka, usuń pozycje, wtedy będziesz mógł wybrać inny dokument."
      );
      return;
    }
    setSelectorShown(true);
  }, [setSelectorShown, locked]);

  // Expose openSelector() to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      openSelector: () => openSelector(),
    }),
    [openSelector]
  );

  const onSelectorOk = (businessDocument: businessDocumentVM) => {
    setSelectorShown(false);
    setValue(businessDocument);
    field.onChange(businessDocument);
    props.onDocumentChanged?.(businessDocument);
  };

  return (
    <>
      <div className="row mb-3">
        <Label
          label={props.label}
          id={props.id}
          isError={!!error && !!isTouched}
          description={props.longDescription}
        ></Label>

        <div className="col-sm-8">
          <InputGroup className="-app-medium-control">
            <Form.Control
              readOnly
              onClick={openSelector}
              id={field.name}
              className={appendValidOrInvalidClassName("", error, isTouched)}
              onBlur={field.onBlur}
              value={field.value?.documentNumber || ""}
              name={field.name}
              ref={field.ref}
            />
            <Button
              variant="outline-secondary"
              title="Wybierz"
              id={`select-${localProps.name}-button`}
              onClick={openSelector}
              disabled={locked}
            >
              Wybierz
            </Button>
          </InputGroup>
          <Description description={props.inlineDescription}></Description>
          <FieldError
            error={error?.message as string | null | undefined}
          ></FieldError>
        </div>
      </div>

      <BusinessDocumentSelectorDialog
        show={isSelectorShown}
        selectorSectionId={props.selectorSectionId}
        ok={onSelectorOk}
        cancel={() => {
          setSelectorShown(false);
        }}
      ></BusinessDocumentSelectorDialog>
    </>
  );
}

// Wrap with forwardRef, then cast to keep generics on the exported component
export const BusinessDocumentSelectorFormRow = forwardRef(
  BusinessDocumentSelectorFormRowInner
) as <T extends FieldValues>(
  props: BusinessDocumentSelectorProps<T> &
    React.RefAttributes<BusinessDocumentSelectorHandle>
) => React.ReactElement;

export default BusinessDocumentSelectorFormRow;
